import { useEffect } from 'react';
import { useParams, useNavigate } from 'react-router';
import { ArrowLeft, Users } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { Card } from '@/components/ui/card';
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger
} from '@/components/ui/tabs';
import { useAccountDetail, useSelectedAccountId } from '@/hooks/use-account';
import { StatusBadge } from '@/modules/accounts/components/status-badge';
import { AccountFieldsForm } from '@/modules/accounts/components/account-fields-form';
import { AccountUsersSection } from '@/modules/accounts/components/account-users-section';
import { BusinessUnitsSection } from '@/modules/accounts/components/business-units-section';
import { ProductsFeaturesSection } from '@/modules/accounts/components/products-features-section';
import { ActivityTimeline } from '@/modules/accounts/components/activity-timeline';
import { TestOrdersSection } from '@/modules/accounts/components/test-orders-section';
import { initials } from '@/lib/utils';

export default function AccountDetailPage() {
  const { accountId } = useParams();
  const navigate = useNavigate();
  const [, setSelectedId] = useSelectedAccountId();
  const { data: account, isLoading, error } = useAccountDetail();

  useEffect(() => {
    setSelectedId(accountId ? Number(accountId) : null);
    return () => setSelectedId(null);
  }, [accountId, setSelectedId]);

  return (
    <div className='mx-auto w-full max-w-5xl space-y-6 p-6 lg:p-8'>
      <Button
        variant='ghost'
        size='sm'
        className='-ml-2 text-muted-foreground'
        onClick={() => navigate('/accounts')}
      >
        <ArrowLeft className='mr-1 h-4 w-4' />
        Back to Accounts
      </Button>

      {error ? (
        <div className='rounded-xl border border-destructive/50 bg-destructive/5 p-4 text-sm text-destructive'>
          {error instanceof Error ? error.message : 'Failed to load account.'}
        </div>
      ) : isLoading || !account ? (
        <>
          <Skeleton className='h-24 w-full rounded-xl' />
          <Skeleton className='h-64 w-full rounded-xl' />
        </>
      ) : (
        <>
          <Card className='flex flex-row items-center gap-4 p-5'>
            <div className='flex h-14 w-14 shrink-0 items-center justify-center rounded-xl bg-primary/10 text-lg font-semibold text-primary'>
              {initials(account.name)}
            </div>
            <div className='min-w-0 flex-1'>
              <div className='flex items-center gap-2'>
                <h1 className='truncate text-xl font-semibold'>
                  {account.name}
                </h1>
                <StatusBadge status={account.account_status} />
              </div>
              <p className='mt-0.5 text-sm text-muted-foreground'>
                {account.sub_domain
                  ? `${account.sub_domain}.kim.cc`
                  : 'No subdomain set'}
              </p>
            </div>
            <div className='hidden shrink-0 items-center gap-2 text-sm text-muted-foreground sm:flex'>
              <Users className='h-4 w-4' />
              {account.userCount} {account.userCount === 1 ? 'user' : 'users'}
            </div>
          </Card>

          <Tabs defaultValue='details'>
            <TabsList>
              <TabsTrigger value='details'>Details</TabsTrigger>
              <TabsTrigger value='users'>Users</TabsTrigger>
              <TabsTrigger value='business-units'>Business Units</TabsTrigger>
              <TabsTrigger value='products'>Products &amp; Features</TabsTrigger>
              <TabsTrigger value='activity'>Activity</TabsTrigger>
              <TabsTrigger value='test-orders'>Test orders</TabsTrigger>
            </TabsList>

            <TabsContent value='details' className='pt-6'>
              <Card className='p-6'>
                <AccountFieldsForm account={account} />
              </Card>
            </TabsContent>
            <TabsContent value='users' className='pt-6'>
              <AccountUsersSection />
            </TabsContent>
            <TabsContent value='business-units' className='pt-6'>
              <BusinessUnitsSection />
            </TabsContent>
            <TabsContent value='products' className='pt-6'>
              <ProductsFeaturesSection
                featureGuards={account.featureGuards}
                aiInsights={account.aiInsights}
                products={account.products}
              />
            </TabsContent>
            <TabsContent value='activity' className='pt-6'>
              <Card className='p-6'>
                <ActivityTimeline />
              </Card>
            </TabsContent>
            <TabsContent value='test-orders' className='pt-6'>
              <TestOrdersSection />
            </TabsContent>
          </Tabs>
        </>
      )}
    </div>
  );
}
